import { AttachFileOutlined, ChatBubbleOutlineOutlined } from "@mui/icons-material";
import { Box, Card, Divider, Tooltip, Typography } from "@mui/material";
import { green, grey, red, yellow } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry } from "ag-grid-enterprise";
import { MasterDetailModule } from "ag-grid-enterprise";
import { isNil, size } from "lodash";
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useLocalStorageState from "use-local-storage-state";
import { nsKey } from "../../utils/storage/partnerStorage";
import { htmlStringToText } from "../../_src_shared/utils/functions/htmlStringToText";
import Flx from "../layout/Flx";
import AgGridTableWrapper from "../agGrid/AgGridTableWrapper";
import CellRendererRequirementType from "../agGrid/cellRenderer/CellRendererRequirementType";
import { useDispatch, useSelector } from "react-redux";
import ColumnDefHeaderLabelWithIcon from "../../_src_shared/agGrid/columnDef/headerComponent/ColumnDefHeaderLabelWithIcon";
import CommentThreadPreviewTooltip from "../tooltips/CommentThreadPreviewTooltip";
import { getDaysAgo } from "../../_src_shared/utils/functions/getDaysAgo";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import Txt from "../typography/Txt";
import { formatDateShort } from "../../utils/dates/formatDateShort";
import { getUserFullNameFromEmail } from "../../utils/functions/getUserFullNameFromEmail";
import { RowDetail } from "../../screens/LoanDrilldownScreen/tabs/LoanDrilldownConditionsChecklistTab/LoanDrilldownConditionsChecklistTab";
import { useChecklistRouteHook } from "../../hooks/useChecklistRouteHook";
import { loanFetchedDataUwRequirementsSet } from "../../redux/reducers/loanFetchedDataReducer";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../redux/actions/sidebarActions";
import { loanDrilldownSidebarTypes } from "../../constants/enums/loanDrilldownScreenSidebarOptions";

// Master Detail is an AG Grid Enterprise feature — register once at module load
// so the conditions table's expandable rows work even if no other enterprise
// table mounted first.
ModuleRegistry.registerModules([MasterDetailModule]);

// Shared localStorage key for this table's remembered view (column order /
// width / visibility / sort). Exported so the Conditions tab header can read
// the same value to know whether to surface a "Reset Table" button.
export const CONDITIONS_TABLE_COLUMN_STATE_KEY = nsKey(
  "loanConditionsTableColumnState",
);

// Left accent bar on an expanded row + its detail panel (matches the checklist
// view's cyan-600 bar).
const EXPANDED_ACCENT_BAR = "#0891b2";

// AG Grid fires column/sort events from many sources (our own
// `applyColumnState`/`resetColumnState` come through as "api", columnDefs
// rebuilds as "columnEverythingChanged", etc.). Only user-driven changes
// should be persisted, so we whitelist the UI sources.
const isUserColumnEventSource = (source) =>
  typeof source === "string" &&
  (source.startsWith("ui") ||
    source === "toolPanelUi" ||
    source === "contextMenu");

// Master-detail expanded row — renders the SAME interactive detail panel as the
// checklist view (RowDetail) and wires its handlers to the checklist route hook,
// so status / doc-status / external edits, comments, files, Manage (full
// sidebar), and delete all work inline from the expanded row.
const ConditionMasterDetailCell = (params) => {
  const condition = params?.data;
  const dispatch = useDispatch();
  const loanId = useSelector((s) => s.loanDrilldown?._id);
  const { updateLoanUnderwritingRequirement, deleteLoanUnderwritingRequirement } =
    useChecklistRouteHook();
  const [pending, setPending] = useState(false);

  const applyResponse = useCallback(
    (response) => {
      const next = Array.isArray(response)
        ? response
        : response?.conditions || response?.comments;
      if (Array.isArray(next)) dispatch(loanFetchedDataUwRequirementsSet(next));
    },
    [dispatch],
  );

  const onPatch = useCallback(
    (data) => {
      if (!condition?._id) return;
      setPending(true);
      updateLoanUnderwritingRequirement({
        dealId: loanId,
        requirementId: condition._id,
        data,
        onSuccessFn: (response) => {
          applyResponse(response);
          setPending(false);
        },
        onErrorFn: () => setPending(false),
      });
    },
    [condition?._id, loanId, updateLoanUnderwritingRequirement, applyResponse],
  );

  const onSetStatus = useCallback(
    (status) => {
      const data = { status };
      if (status === "Accepted" && condition?.requirementType === "Document") {
        data.docStipStatus = "Accepted";
      }
      onPatch(data);
    },
    [onPatch, condition?.requirementType],
  );

  const onManage = useCallback(() => {
    dispatch(
      sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EDIT_LOAN_CONDITION_REDESIGN),
    );
    dispatch(sidebarSetStateValue(condition));
  }, [dispatch, condition]);

  const onDelete = useCallback(() => {
    if (!condition?._id) return;
    setPending(true);
    deleteLoanUnderwritingRequirement({
      dealId: loanId,
      requirementId: condition._id,
      onSuccessFn: (response) => {
        if (response?.conditions) {
          dispatch(loanFetchedDataUwRequirementsSet(response.conditions));
        } else {
          applyResponse(response);
        }
        setPending(false);
      },
      onErrorFn: () => setPending(false),
    });
  }, [
    condition?._id,
    loanId,
    deleteLoanUnderwritingRequirement,
    dispatch,
    applyResponse,
  ]);

  if (!condition) return null;
  return (
    // Left accent bar on the expanded detail, matching the checklist view.
    <Box sx={{ borderLeft: `3px solid ${EXPANDED_ACCENT_BAR}` }}>
      <RowDetail
        item={condition}
        pending={pending}
        onPatch={onPatch}
        onSetStatus={onSetStatus}
        onManage={onManage}
        onDelete={onDelete}
      />
    </Box>
  );
};

const LoanUnderwritingConditionsTable = ({
  quickFilter,
  rowData,
  onRowClicked,
}) => {
  const gridRef = useRef(null);
  // Remembered table view — persisted to localStorage and re-applied on
  // mount, mirroring the Documents table. No default sort is stamped; we
  // simply restore whatever arrangement (incl. sort) the user left behind.
  const [savedColumnState, setSavedColumnState] = useLocalStorageState(
    CONDITIONS_TABLE_COLUMN_STATE_KEY,
    { defaultValue: null },
  );
  const hasDocStipStatus = useMemo(() => {
    const ACTIONABLE = new Set([
      "Open Item",
      "Received - Pending Review",
      "Reviewed - Not Accepted",
    ]);
    return rowData?.some((row) => ACTIONABLE.has(row?.docStipStatus));
  }, [rowData]);
  const hasSecondLevelReview = useMemo(() => {
    return rowData?.some((row) => row?.status === "Second Level Review");
  }, [rowData]);
  const hasResolution = useMemo(() => {
    return rowData?.some((row) => row?.resolutionNotes || row?.resolvedBy);
  }, [rowData]);
  const condition = useSelector((state) => state?.sidebar?.state);
  const users = useSelector((state) => state.users);
  const columnDefs = useMemo(
    () => [
      {
        // Type cell carries the master-detail expand/collapse chevron via
        // agGroupCellRenderer (which auto-manages the open/closed arrow); the
        // requirement-type chip renders as its inner content. Row click also
        // toggles expansion.
        headerName: "Type",
        field: "requirementType",
        width: 150,
        // cellClass: "ag-cell-vertical-center",
        tooltipField: "requirementType",
        tooltipValueGetter: (p) => {
          return p?.data?.requirementType;
        },
        cellStyle: { cursor: "pointer", display: "flex", alignItems: "center" },
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: (params) => (
            <CellRendererRequirementType value={params.value} />
          ),
        },
      },
      {
        headerName: "Status",
        field: "status", // ['Open', 'In Progress',  'Accepted', 'Rejected', 'Second Level Review']
        width: hasSecondLevelReview ? 150 : 120,
        cellStyle: {
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
        },
        // cellClass: "ag-cell-vertical-center",
      },

      {
        headerName: "Document Status",
        field: "docStipStatus", // ["Open Item", "Received - Pending Review", "Reviewed - Not Accepted", "Accepted"]
        width: 190,
        // sort: "desc",
        hide: !hasDocStipStatus,
        // cellClass: "ag-cell-vertical-center",
        tooltipComponent: null,
        valueFormatter: (p) => {
          const status = p.value;

          if (isNil(status)) {
            return "N/A";
          }
          return status;
        },
        cellStyle: (params) => {
          const status = params.value;
          // let backgroundColor = green[100];
          let backgroundColor = "transparent";

          if (status === "Received - Pending Review") {
            backgroundColor = yellow[50];
          }
          if (status === "Reviewed - Not Accepted") {
            backgroundColor = red[50];
          }
          if (status === "Accepted") {
            backgroundColor = green[50];
          }
          return {
            cursor: "pointer",
            // opacity: isNil(status) ? 0.65 : 1,
            display: "flex",
            alignItems: "center",
            fontStyle:
              isNil(status) || status === "Open Item" ? "normal" : "italic",
            backgroundColor,
          };
        },
      },

      {
        // Wraps the full requirement text. The interactive controls (comments,
        // files, status) live in the expandable master-detail row below.
        headerName: "Requirement Details",
        field: "htmlString",
        minWidth: 250,
        flex: 1,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          cursor: "pointer",
          whiteSpace: "normal",
          lineHeight: 1.5,
          paddingTop: 10,
          paddingBottom: 10,
        },
        valueFormatter: (p) => {
          if (isNil(p.value) || p.value === "") {
            return "N/A";
          }
          return htmlStringToText(p.value);
        },
        cellRenderer: (p) => {
          const preview =
            isNil(p.value) || p.value === "" ? "N/A" : htmlStringToText(p.value);
          const docTitle = p?.data?.docType
            ? p.data.docType
            : null;
          const comments = p?.data?.comments || p?.data?.conditionComments;
          const commentCount = size(comments);
          const docFileCount = size(p?.data?.uploadedDocumentIds);
          return (
            <Flx column g={0.6} sx={{ py: 0.25 }}>
              <Txt
                component="div"
                sx={{
                  fontSize: 12.5,
                  color: "#334155",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.5,
                }}
              >
                {docTitle ? (
                  <>
                    <b>{docTitle}</b>
                    {preview && preview !== "N/A" ? ` — ${preview}` : ""}
                  </>
                ) : (
                  preview
                )}
              </Txt>
              {/* Comment / document associations surface as chips under the
                  details (the standalone Interactions column was removed). */}
              {(commentCount > 0 || docFileCount > 0) && (
                <Flx ac g={0.5} wrap>
                  {commentCount > 0 && (
                    <CommentThreadPreviewTooltip comments={comments}>
                      <MetaChip
                        icon={
                          <ChatBubbleOutlineOutlined sx={{ fontSize: 13 }} />
                        }
                        count={commentCount}
                        label={commentCount === 1 ? "comment" : "comments"}
                      />
                    </CommentThreadPreviewTooltip>
                  )}
                  {docFileCount > 0 && (
                    <Tooltip title={getDocumentCountTooltipText(p)}>
                      <MetaChip
                        icon={<AttachFileOutlined sx={{ fontSize: 13 }} />}
                        count={docFileCount}
                        label={docFileCount === 1 ? "document" : "documents"}
                      />
                    </Tooltip>
                  )}
                </Flx>
              )}
            </Flx>
          );
        },
      },

      {
        headerName: "Updated",
        field: "updateDateTime",
        // sort: "desc",
        width: 150,
        autoHeight: true,
        cellStyle: { paddingTop: 8, paddingBottom: 8 },
        // cellClass: "ag-cell-vertical-center",
        tooltipComponent: (params) => (
          <Flx
            ac
            g={1}
            sx={{
              background: "#ffffff",
              border: `1px solid ${grey[300]}`,
              p: 1,
              borderRadius: 2,
            }}
          >
            Last Updated <b>{formatDateTime(params.data?.updateDateTime)}</b> (
            {getDaysAgo(params.data?.updateDateTime) === 0
              ? `Today ${isNil(params.data?.updatedBy) ? "" : `by ${params.data?.updatedBy}`}`
              : getDaysAgo(params.data?.updateDateTime) === 1
                ? `1 day ago ${isNil(params.data?.updatedBy) ? "" : `by ${params.data?.updatedBy}`}`
                : `${getDaysAgo(params.data?.updateDateTime)} days ago ${isNil(params.data?.updatedBy) ? "" : `by ${params.data?.updatedBy}`}`}
            )
          </Flx>
        ),
        tooltipValueGetter: (params) => {
          // return params.data?.updateDateTime;
          return `Last Updated: ${formatDateTime(params.data?.updateDateTime)} (${
            getDaysAgo(params.data?.updateDateTime) === 0
              ? `Today ${isNil(params.data?.updatedBy) ? "" : `by ${params.data?.updatedBy}`}`
              : getDaysAgo(params.data?.updateDateTime) === 1
                ? `1 day ago ${isNil(params.data?.updatedBy) ? "" : `by ${params.data?.updatedBy}`}`
                : `${getDaysAgo(params.data?.updateDateTime)} days ago ${isNil(params.data?.updatedBy) ? "" : `by ${params.data?.updatedBy}`}`
          })`;
        },
        // cellStyle: {
        //   // lineHeight: 1.5,
        //   cursor: "pointer",
        //   // paddingTop: 10,
        //   // paddingBottom: 10,
        //   // display: "flex",
        //   // alignItems: "center",
        //   // wrapText: true, // Wrap Text
        //   // whiteSpace: "normal", // Allow normal white space handling
        //   // autoHeight: true, // Adjust Cell Height to Fit Wrapped Text
        // },
        cellRenderer: (p) => {
          // Mirror the document-table status cell: a neutral pill (here the
          // updated date) on top, "By {updatedBy}" byline below.
          const dateStr = isNil(p?.value) ? "—" : formatDateShort(p.value);
          const byName = p?.data?.updatedBy
            ? getUserFullNameFromEmail(users, p.data.updatedBy)
            : null;
          return (
            <Flx column g={0.25} sx={{ minWidth: 0 }}>
              <Flx
                ac
                g={0.5}
                sx={{
                  display: "inline-flex",
                  alignSelf: "flex-start",
                  px: 0.75,
                  py: 0.25,
                  borderRadius: "6px",
                  bgcolor: "#F1EFE8",
                  color: "#5F5E5A",
                  border: "1px solid #D3D1C7",
                }}
              >
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    bgcolor: "#B4B2A9",
                    flexShrink: 0,
                  }}
                />
                <Txt sx={{ fontSize: "11px", color: "#5F5E5A", fontWeight: 500 }}>
                  {dateStr}
                </Txt>
              </Flx>
              {byName && (
                <Txt
                  sx={{
                    fontSize: "9.5px",
                    color: grey[600],
                    lineHeight: 1.3,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  By {byName}
                </Txt>
              )}
            </Flx>
          );
        },
      },
      {
        width: 170,
        field: "resolvedBy",
        headerComponent: ColumnDefHeaderLabelWithIcon,
        headerTooltip:
          "Resolved: Indicates whether this condition was resolved, and by whom / when.",
        tooltipValueGetter: (params) => {
          return params.data;
        },
        tooltipComponent: (p) => {
          const notes = p?.data?.resolutionNotes;
          const resolvedBy = p?.data?.resolvedBy;
          return (
            <Card sx={{ p: 1.5, maxWidth: "400px" }}>
              <Flx column g={1}>
                <Txt>
                  Resolved By: <b>{resolvedBy || "Unknown"}</b>
                </Txt>
                <Divider />
                <Flx g={0.5}>
                  <Txt>Notes:</Txt>
                  <Txt bold> {notes}</Txt>
                </Flx>
              </Flx>
            </Card>
          );
        },
        headerName: "Resolved",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          cursor: "pointer",
          whiteSpace: "normal",
          lineHeight: 1.3,
          paddingTop: 6,
          paddingBottom: 6,
          display: "flex",
          alignItems: "center",
        },
        hide: !hasResolution,
        valueGetter: (p) =>
          !!(
            p?.data?.resolutionNotes ||
            p?.data?.resolvedBy ||
            p?.data?.resolvedDateTime
          ),
        cellRenderer: (p) => {
          if (!p.value) return <YesNoText value={false} />;
          const who = p?.data?.resolvedBy
            ? getUserFullNameFromEmail(users, p.data.resolvedBy, "Unknown")
            : null;
          const when = p?.data?.resolvedDateTime
            ? formatDateTime(p.data.resolvedDateTime)
            : null;
          return (
            <Flx column g={0.1} sx={{ py: 0.25 }}>
              <Txt
                sx={{ fontSize: 12, fontWeight: 600, color: green[700] }}
              >
                Resolved
              </Txt>
              {(who || when) && (
                <Txt sx={{ fontSize: 10.5, color: grey[600], lineHeight: 1.3 }}>
                  {when}
                  {who ? `${when ? " · " : ""}${who}` : ""}
                </Txt>
              )}
            </Flx>
          );
        },
      },
      {
        width: 120,
        // cellClass: "ag-cell-vertical-center",
        field: "displayExternally",
        headerComponent: ColumnDefHeaderLabelWithIcon,
        headerTooltip:
          "External: Indicates whether this condition is displayed externally with the counterparty.",
        tooltipValueGetter: (p) => {
          if (p?.data?.displayExternally) {
            return "This condition is displayed externally with the counterparty.";
          } else {
            return "This is an INTERNAL only condition that can not be seen and is not displayed externally with a loan counterparty.";
          }
        },
        headerName: "External",
        cellStyle: {
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
        },
        valueGetter: (p) => {
          if (
            isNil(p?.data?.displayExternally) ||
            !p?.data?.displayExternally
          ) {
            return false;
          } else {
            return true;
          }
        },
        cellRenderer: (p) => <YesNoText value={p.value} />,
      },
      {
        width: 90,
        // cellClass: "ag-cell-vertical-center",
        headerName: "Auto",
        headerComponent: ColumnDefHeaderLabelWithIcon,
        headerComponentParams: { menuIcon: "fa-cog" },
        headerTooltip:
          "Auto: Indicates whether this condition was auto-generated by the system or manually added by a user.",
        tooltipValueGetter: (p) => {
          if (p?.data?.autoGenerated) {
            return "This condition was auto-generated by the system.";
          } else {
            return `This condition was manually added by ${p?.data?.createdBy}`;
          }
        },
        field: "autoGenerated",
        cellStyle: {
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
        },
        valueGetter: (p) => {
          if (isNil(p?.data?.autoGenerated) || !p?.data?.autoGenerated) {
            return false;
          } else {
            return true;
          }
        },
        cellRenderer: (p) => <YesNoText value={p.value} />,
      },
    ],
    [condition, users, hasDocStipStatus, hasSecondLevelReview, hasResolution],
  );

  const getRowId = useCallback((params) => params.data._id, []);

  const getRowStyle = useCallback(
    (params) => {
      const isActive = condition?._id === params?.data?._id;
      // if (condition?._id === params?.data?._id) {
      //   return {
      //     // backgroundColor: "#e3f2fd63",
      //     fontWeight: 600,
      //     borderLeft: `4px solid ${blue[500]}`,
      //   };
      // }

      const status = params.data?.status;
      const docStatus = params.data?.docStipStatus;

      let backgroundColor = "transparent";
      let borderColor = "transparent";

      if (status === "Rejected" || docStatus === "Reviewed - Not Accepted") {
        // RED
        backgroundColor = "#ffebeea6";
        borderColor = red[100];
      }

      if (
        status === "Second Level Review" ||
        status === "In Progress" ||
        docStatus === "Received - Pending Review"
      ) {
        // YELLOW
        backgroundColor = "#fffde7a6";
        borderColor = yellow[100];
      }

      if (status === "Accepted") {
        backgroundColor = "#e8f5e9a6";
        borderColor = green[100];
      }
      // Expanded master rows get the cyan accent bar so it reads continuously
      // with the detail panel's left border below.
      const expanded = !!params?.node?.expanded;
      return {
        backgroundColor,
        fontWeight: isActive ? 600 : 400,
        borderLeft: expanded
          ? `3px solid ${EXPANDED_ACCENT_BAR}`
          : isActive
            ? `4px solid #1F5AF2`
            : "none",
        borderRight: isActive ? `4px solid #1F5AF2` : "none",
        borderTop: `1px solid ${borderColor}`,
      };
      return {
        backgroundColor: "#fffde7a6",
        // backgroundColor: yellow[50],
        borderTop: `1px solid #ffffff`,
        borderBottom: `1px solid #ffffff`,
      };
    },
    [condition],
  );

  const defaultColDef = useMemo(
    () => ({
      cursor: "pointer",
      // Drop the per-header filter + menu (⋮) buttons — there are no column
      // filters here, and the icons were eating into the label width and
      // truncating the header names.
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true,
      // tooltipComponent: () => (
      //   <Flx
      //     ac
      //     g={1}
      //     sx={{
      //       background: "#ffffff",
      //       border: `1px solid ${grey[300]}`,
      //       p: 1,
      //       borderRadius: 2,
      //     }}
      //   >
      //     Click row to address Loan Condition
      //     <ChevronRightRounded sx={{ color: grey[600] }} />
      //   </Flx>
      // ),
      // tooltipValueGetter: () => "Click to address condition",
    }),
    [],
  );

  // Clicking a row toggles its expandable master-detail (same as the checklist
  // view). The detail panel's "Edit"/"Manage" links open the full sidebar; the
  // legacy onRowClicked prop is kept for callers but no longer fires on click.
  const handleOnRowClicked = useCallback(
    (params) => {
      if (!params?.node) return;
      params.node.setExpanded(!params.node.expanded);
    },
    [],
  );

  const detailCellRenderer = useCallback(ConditionMasterDetailCell, []);

  // Re-apply the remembered view once the grid is ready. `applyColumnState`
  // fires column events with source "api", so it won't trip the persist
  // handler below.
  const handleGridReady = useCallback(
    (params) => {
      if (Array.isArray(savedColumnState) && savedColumnState.length) {
        params.api.applyColumnState({
          state: savedColumnState,
          applyOrder: true,
        });
      }
    },
    [savedColumnState],
  );

  // Persist the full column state (order / width / visibility / sort) on any
  // user-driven column or sort change.
  const persistColumnState = useCallback(
    (event) => {
      if (!isUserColumnEventSource(event?.source)) return;
      // Resize fires continuously while dragging — only persist the final size.
      if (event?.type === "columnResized" && event?.finished === false) return;
      const api = gridRef.current?.api;
      if (!api) return;
      setSavedColumnState(api.getColumnState());
    },
    [setSavedColumnState],
  );

  // When the remembered view is cleared (e.g. the tab's Reset Table button
  // sets the shared key to null), snap the grid back to its column defaults.
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    if (!savedColumnState) {
      api.resetColumnState();
    }
  }, [savedColumnState]);

  return (
    <Box
      sx={{
        // maxHeight: "800px",
        overflowY: "auto",
        // border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: "8px",
        ".ag-row-last": {
          borderBottom: "none !important",
        },
        ".ag-header-viewport": {
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <AgGridTableWrapper
        height="100%"
        // simpleTable
        compactTable
        suppressMinHeightWhenPopulated={rowData}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          quickFilterText={quickFilter}
          getRowId={getRowId}
          defaultColDef={defaultColDef}
          getRowStyle={getRowStyle}
          onRowClicked={handleOnRowClicked}
          onGridReady={handleGridReady}
          // Redraw the master row on expand/collapse so getRowStyle re-runs and
          // the cyan accent bar appears/disappears with the detail panel.
          onRowGroupOpened={(e) =>
            e.api.redrawRows({ rowNodes: [e.node] })
          }
          onColumnMoved={persistColumnState}
          onColumnResized={persistColumnState}
          onColumnVisible={persistColumnState}
          onColumnPinned={persistColumnState}
          onSortChanged={persistColumnState}
          domLayout="autoHeight"
          masterDetail
          detailCellRenderer={detailCellRenderer}
          detailRowAutoHeight
          keepDetailRows
          tooltipShowDelay={100}
          enableCellTextSelection
          //   tooltipMouseTrack
        />
      </AgGridTableWrapper>
    </Box>
  );
};

// Plain Yes/No cell — replaces the disabled checkboxes in the Resolved /
// External / Auto columns. Green "Yes" reads as affirmative at a glance; muted
// grey "No" stays quiet.
const YesNoText = ({ value }) => (
  <Txt
    sx={{
      fontSize: 12,
      fontWeight: 600,
      color: value ? green[700] : grey[500],
    }}
  >
    {value ? "Yes" : "No"}
  </Txt>
);

// Small chip shown under the requirement details for comment / document
// associations (replaces the standalone Interactions column). forwardRef so
// MUI Tooltip can attach to it.
const MetaChip = memo(
  forwardRef(({ icon, count, label }, ref) => (
    <Box
      ref={ref}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        px: 0.75,
        py: 0.2,
        borderRadius: "6px",
        bgcolor: "#f1f5f9",
        border: "1px solid #e2e8f0",
        color: "#475569",
        lineHeight: 1,
      }}
    >
      {icon}
      <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
        {count}
        {label ? ` ${label}` : ""}
      </Typography>
    </Box>
  )),
);

const getDocumentCountTooltipText = (p) => {
  if (size(p?.data?.uploadedDocumentIds) === 0) {
    return "No documents currently associated with this condition.";
  }
  if (size(p?.data?.uploadedDocumentIds) === 1) {
    return "(1) condition document .";
  }
  return `(${size(p?.data?.uploadedDocumentIds)}) condition documents`;
};
export default LoanUnderwritingConditionsTable;
